/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { css } from '@emotion/react'
import { Popover } from 'antd'
import { EnvironmentOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import colors from '@sportninja/common/constants/appColors'
import type { GeneratedGame } from './GeneratedGamesSection'
import type { Venue } from '../hooks/useSchedulerLocations'

const POPOVER_OVERLAY_STYLE = { zIndex: 1050 }
const POPOVER_INNER_STYLE = {
  padding: 0,
  background: colors.SECONDARY_300,
  border: `1px solid ${colors.SECONDARY_200}`,
}

// ─── Constants ────────────────────────────────────────────────────
const GRID_BORDER_COLOR = '#2f3847'
const APPROVED_COLOR = '#10B981'

const VENUE_COLORS = [
  '#4a90e2', // Blue
  '#e2994a', // Orange
  '#9b4ae2', // Purple
  '#4ae2a8', // Green
  '#e24a6e', // Rose
  '#4acde2', // Cyan
  '#e2d44a', // Gold
  '#7ae24a', // Lime
]

// ─── Types ────────────────────────────────────────────────────────
interface AgendaViewProps {
  games: GeneratedGame[]
  venues: Venue[]
  blackoutDates?: Set<string>
  onGameClick?: (game: GeneratedGame) => void
  popoverGameId?: string | null
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
}

interface DayGroup {
  dateStr: string
  displayDate: string
  games: GeneratedGame[]
  isBlackout: boolean
  isToday: boolean
}

// ─── Helpers ──────────────────────────────────────────────────────
function buildVenueColorMap(venues: Venue[]): Map<string, string> {
  const map = new Map<string, string>()
  venues.forEach((venue, idx) => {
    map.set(venue.id, VENUE_COLORS[idx % VENUE_COLORS.length])
  })
  return map
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function getDateKey(game: GeneratedGame): string {
  if (game.startsAt) {
    return dayjs.utc(game.startsAt).format('YYYY-MM-DD')
  }
  const parsed = dayjs(game.date, 'ddd, MMM D, YYYY')
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : game.date
}

// ─── Day container style ─────────────────────────────────────────
const dayContainerStyles = css`
  border: 0.8px solid ${GRID_BORDER_COLOR};
  border-radius: 8px;
  overflow: hidden;
`

// ─── Sub-components ───────────────────────────────────────────────

const AgendaDayHeader: React.FC<{
  displayDate: string
  gameCount: number
  isToday: boolean
  isBlackout: boolean
}> = ({ displayDate, gameCount, isToday, isBlackout }) => (
  <div
    css={css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background: ${isToday ? 'rgba(242, 180, 0, 0.13)' : 'transparent'};
      border-bottom: 0.8px solid ${GRID_BORDER_COLOR};
    `}
  >
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 2px;
      `}
    >
      <span
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 14px;
          line-height: 21px;
          color: ${isToday ? colors.PRIMARY_300 : colors.WHITE};
        `}
      >
        {displayDate}
      </span>
      <span
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 11px;
          line-height: 16.5px;
          color: ${isToday ? colors.NEUTRAL_50 : '#797b7e'};
        `}
      >
        {isBlackout && gameCount === 0
          ? 'Blackout day'
          : `${gameCount} game${gameCount !== 1 ? 's' : ''} scheduled`}
      </span>
    </div>
    {isToday && (
      <span
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 10px;
          letter-spacing: 1px;
          line-height: 15px;
          color: #0f1928;
          background: ${colors.PRIMARY};
          border-radius: 4px;
          padding: 4px 12px;
          text-transform: uppercase;
          flex-shrink: 0;
        `}
      >
        TODAY
      </span>
    )}
  </div>
)

const AgendaGameRow: React.FC<{
  game: GeneratedGame
  venueColor: string
}> = ({ game, venueColor }) => {
  const isApproved = game.approvalStatus === 'approved'
  const accentColor = isApproved ? APPROVED_COLOR : venueColor
  const displayColor = isApproved ? APPROVED_COLOR : venueColor

  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        align-items: flex-start;
        padding: 16px;
        border-bottom: 0.8px solid ${GRID_BORDER_COLOR};
        &:hover {
          background: ${colors.SECONDARY_200};
        }
      `}
    >
      {/* Time column */}
      <div
        css={css`
          width: 80px;
          flex-shrink: 0;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 13px;
          line-height: 19.5px;
          color: ${colors.WHITE};
        `}
      >
        {game.time}
      </div>

      {/* Content area */}
      <div
        css={css`
          flex: 1;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          min-width: 0;
        `}
      >
        {/* Accent bar + details */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
          `}
        >
          {/* Accent bar */}
          <div
            css={css`
              width: 4px;
              height: 40px;
              border-radius: 2px;
              background: ${accentColor};
              flex-shrink: 0;
            `}
          />

          {/* Details */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 4px;
              min-width: 0;
            `}
          >
            {/* Team line */}
            <div
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 600;
                font-size: 13px;
                line-height: 19.5px;
                color: ${colors.WHITE};
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              `}
            >
              {game.homeTeam}{' '}
              <span
                css={css`
                  color: #797b7e;
                  font-weight: 400;
                `}
              >
                vs
              </span>{' '}
              {game.awayTeam}
            </div>

            {/* Venue info line */}
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 12px;
              `}
            >
              {/* Pin icon + venue name */}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                `}
              >
                <EnvironmentOutlined
                  css={css`
                    font-size: 12px;
                    color: ${displayColor};
                    flex-shrink: 0;
                  `}
                />
                <span
                  css={css`
                    font-family: 'Inter', sans-serif;
                    font-weight: 500;
                    font-size: 11px;
                    line-height: 16.5px;
                    color: ${displayColor};
                    white-space: nowrap;
                  `}
                >
                  {game.location}
                </span>
              </div>
              {/* Facility name */}
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 400;
                  font-size: 11px;
                  line-height: 16.5px;
                  color: #797b7e;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {game.surface}
              </span>
            </div>
          </div>
        </div>

        {/* Game number badge */}
        {game.gameNumber != null && (
          <div
            css={css`
              flex-shrink: 0;
              font-family: 'Inter', sans-serif;
              font-weight: 600;
              font-size: 10px;
              line-height: 15px;
              color: ${displayColor};
              background: ${hexToRgba(displayColor, 0.13)};
              border-radius: 4px;
              padding: 4px 10px;
              white-space: nowrap;
            `}
          >
            #{game.gameNumber}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────
const AgendaView: React.FC<AgendaViewProps> = ({
  games,
  venues,
  blackoutDates,
  onGameClick,
  popoverGameId,
  popoverContent,
  onPopoverOpenChange,
}) => {
  const venueColorMap = useMemo(() => buildVenueColorMap(venues), [venues])

  const todayStr = useMemo(() => dayjs().format('YYYY-MM-DD'), [])

  const dayGroups = useMemo(() => {
    const gamesByDate = new Map<string, GeneratedGame[]>()
    for (const game of games) {
      const key = getDateKey(game)
      if (!gamesByDate.has(key)) {
        gamesByDate.set(key, [])
      }
      gamesByDate.get(key)!.push(game)
    }

    // Include blackout dates within the game date range
    const allDateKeys = new Set(gamesByDate.keys())
    if (blackoutDates && allDateKeys.size > 0) {
      const sortedGameDates = [...allDateKeys].sort()
      const minDate = sortedGameDates[0]
      const maxDate = sortedGameDates[sortedGameDates.length - 1]
      for (const bd of blackoutDates) {
        if (bd >= minDate && bd <= maxDate) {
          allDateKeys.add(bd)
        }
      }
    }

    const sortedDates = [...allDateKeys].sort()

    return sortedDates.map((dateStr): DayGroup => {
      const dateGames = gamesByDate.get(dateStr) ?? []
      dateGames.sort((a, b) => {
        if (a.startsAt && b.startsAt) {
          return a.startsAt.localeCompare(b.startsAt)
        }
        return a.time.localeCompare(b.time)
      })

      return {
        dateStr,
        displayDate: dayjs(dateStr, 'YYYY-MM-DD').format('dddd, MMMM D'),
        games: dateGames,
        isBlackout: blackoutDates?.has(dateStr) ?? false,
        isToday: dateStr === todayStr,
      }
    })
  }, [games, blackoutDates, todayStr])

  if (games.length === 0) {
    return (
      <div
        css={[
          dayContainerStyles,
          css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 32px 16px;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            color: ${colors.NEUTRAL_50};
          `,
        ]}
      >
        No games to display.
      </div>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      {dayGroups.map((group) => (
        <div key={group.dateStr} css={dayContainerStyles}>
          <AgendaDayHeader
            displayDate={group.displayDate}
            gameCount={group.games.length}
            isToday={group.isToday}
            isBlackout={group.isBlackout}
          />
          {group.isBlackout && group.games.length === 0 ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 32px 16px;
                gap: 4px;
              `}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 600;
                  font-size: 12px;
                  letter-spacing: 1px;
                  color: ${colors.NEUTRAL_100};
                  text-transform: uppercase;
                `}
              >
                BLACKOUT DAY
              </span>
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 11px;
                  color: ${colors.NEUTRAL_50};
                `}
              >
                No games scheduled
              </span>
            </div>
          ) : (
            group.games.map((game) => {
              const venueColor =
                venueColorMap.get(game.locationId ?? '') ?? VENUE_COLORS[0]
              const clickable = game.approvalStatus !== 'approved'
              const isOpen = popoverGameId === game.id

              if (onPopoverOpenChange) {
                return (
                  <Popover
                    key={game.id}
                    content={isOpen ? popoverContent : null}
                    trigger='click'
                    open={isOpen}
                    onOpenChange={(visible) => {
                      if (visible && !clickable) {
                        return
                      }
                      if (visible) {
                        onGameClick?.(game)
                      }
                      onPopoverOpenChange(visible ? game.id : null)
                    }}
                    placement='right'
                    overlayStyle={POPOVER_OVERLAY_STYLE}
                    overlayInnerStyle={POPOVER_INNER_STYLE}
                    destroyTooltipOnHide
                  >
                    <div
                      css={css`
                        cursor: ${clickable ? 'pointer' : 'default'};
                      `}
                    >
                      <AgendaGameRow game={game} venueColor={venueColor} />
                    </div>
                  </Popover>
                )
              }

              return (
                <div
                  key={game.id}
                  onClick={
                    clickable && onGameClick
                      ? () => onGameClick(game)
                      : undefined
                  }
                  css={css`
                    cursor: ${clickable && onGameClick ? 'pointer' : 'default'};
                  `}
                >
                  <AgendaGameRow game={game} venueColor={venueColor} />
                </div>
              )
            })
          )}
        </div>
      ))}
    </div>
  )
}

export default AgendaView
